import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import type { BrandId, BrandConfig } from "@/types"
import { getBrandConfig, getBrandFromHostname, defaultBrand } from "@/config/brands"

interface BrandState {
  brandId: BrandId
  config: BrandConfig
  brandConfig: BrandConfig // Alias for config
  isDark: boolean
  
  // Actions
  setBrand: (brandId: BrandId) => void
  toggleDarkMode: () => void
  setDarkMode: (isDark: boolean) => void
  initializeBrand: () => void
}

// Initial brand: si estamos en browser, deriva del hostname (evita flash con
// el `defaultBrand` cuando entras a red.celink.mx); en SSR o tests usa default.
const initialBrand: BrandId =
  typeof window !== "undefined" ? getBrandFromHostname() : defaultBrand

export const useBrandStore = create<BrandState>()(
  persist(
    (set, get) => ({
      brandId: initialBrand,
      config: getBrandConfig(initialBrand),
      brandConfig: getBrandConfig(initialBrand), // Alias
      isDark: false,
      
      setBrand: (brandId: BrandId) => {
        const config = getBrandConfig(brandId)
        set({ brandId, config, brandConfig: config })
        
        // Update DOM
        document.documentElement.setAttribute("data-brand", brandId)
      },
      
      toggleDarkMode: () => {
        const newDark = !get().isDark
        set({ isDark: newDark })
        
        if (newDark) {
          document.documentElement.classList.add("dark")
        } else {
          document.documentElement.classList.remove("dark")
        }
      },
      
      setDarkMode: (isDark: boolean) => {
        set({ isDark })
        
        if (isDark) {
          document.documentElement.classList.add("dark")
        } else {
          document.documentElement.classList.remove("dark")
        }
      },
      
      initializeBrand: () => {
        // Auto-detect brand from hostname
        const detectedBrand = getBrandFromHostname()
        const config = getBrandConfig(detectedBrand)
        
        set({ brandId: detectedBrand, config, brandConfig: config })
        document.documentElement.setAttribute("data-brand", detectedBrand)
        
        // Initialize dark mode
        const { isDark } = get()
        if (isDark) {
          document.documentElement.classList.add("dark")
        }
      },
    }),
    {
      name: "brand-storage",
      // Bumpear `version` invalida localStorage previo en el cliente cuando
      // cambia el shape del catálogo `brands` (e.g. eliminamos `flashpack`,
      // cambiamos primaryColor de pandamovil). Sin esto, usuarios con cache
      // verían branding stale tras un deploy.
      version: 2,
      storage: createJSONStorage(() => localStorage),
      // Solo persistimos el brandId y el dark mode. `config`/`brandConfig`
      // son derivados — se recalculan desde el catálogo `brands` (puede haber
      // cambiado entre versiones del frontend).
      partialize: (state) => ({
        brandId: state.brandId,
        isDark: state.isDark,
      }),
      // Tras rehidratar, recalcular `config`/`brandConfig` desde `brandId`
      // (sino quedan undefined → SplashScreen y LoginPage no muestran logo).
      // Además: si el `brandId` persistido NO coincide con el del hostname
      // actual (usuario abrió otra marca en mismo browser), forzamos el
      // brand del hostname — evita branding cruzado.
      onRehydrateStorage: () => (state) => {
        if (!state) return
        const fromHost =
          typeof window !== "undefined" ? getBrandFromHostname() : defaultBrand
        if (state.brandId !== fromHost) {
          state.brandId = fromHost
        }
        const cfg = getBrandConfig(state.brandId)
        state.config = cfg
        state.brandConfig = cfg
      },
    }
  )
)
